import {RootStore} from "store/useRootStore"
import {makeAutoObservable, toJS} from "mobx"
import {
    DeviceStatus,
    isEmergencyStatus,
    isErrorStatus,
    isNoneOperationStatus,
    isPowerDisconnectedStatus,
    RealDevice
} from "dto/realdevice/devices"
import dayjs from "dayjs"
import {PathTrackingStore} from "store/dashboard/PathTrackingStore"
import {ImpactHistoryStore} from "store/dashboard/ImpactHistoryStore"
import {NotificationType, showNotification} from "common/Notification"
import {isValidStringCoordinate, getCoordinateErrorMessage} from "utils/coordinate-validator"

export interface Counter {
    count: number
    normalCount: number
    powerOffCount: number
    emergencyCount: number
    errorCount: number
    workingCount: number
    ttlCount: number
    noneOperationCount: number
    powerDisconnectedCount: number
}

export function newCounter(): Counter {
    return {
        count: 0,
        normalCount: 0,
        emergencyCount: 0,
        errorCount: 0,
        workingCount: 0,
        powerOffCount: 0,
        noneOperationCount: 0,
        powerDisconnectedCount: 0,
        ttlCount: 0,
    }
}

export function calculateDeviceStatus(device: RealDevice, counter: Counter) {
    if (isEmergencyStatus(device)) {
        counter.emergencyCount = counter.emergencyCount + 1
    } else if (isPowerDisconnectedStatus(device)) {
        counter.powerDisconnectedCount = counter.powerDisconnectedCount + 1
    } else if (isErrorStatus(device)) {
        counter.errorCount = counter.errorCount + 1
    } else if (isNoneOperationStatus(device)) {
        counter.noneOperationCount = counter.noneOperationCount + 1
    } else {
        if (device.communicationDevice.status === "WORKING") {
            counter.workingCount = counter.workingCount + 1
        }
        counter.normalCount = counter.normalCount + 1
    }
    return counter
}

export type DeviceStatusCategory = "ALL" | "NORMAL" | "NON_OPERATION" | "ERROR" | "EMERGENCY" | "POWER_DISCONNECTED"

export class DashboardStore {
    map?: kakao.maps.Map
    rootStore: RootStore
    pathTrackingStore: PathTrackingStore
    impactHistoryStore: ImpactHistoryStore
    selectedDevice?: RealDevice = undefined
    deviceStatusFilterCondition: DeviceStatus[] | null = null
    showRealDeviceInfo: boolean = false
    showPathTrackingInfo: boolean = false
    showImpactHistory: boolean = false
    searchKeyword: string | undefined = undefined
    counter: Counter
    conditionToHide: Map<string, Date> = new Map()
    showDeviceStatusInfoList: boolean = true
    showStatusChart: boolean = true
    showEventCount: boolean = false
    showTtlDeviceStatus: boolean = false

    deviceStatusCategory: DeviceStatusCategory = "ALL"

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        this.counter = {
            count: 0,
            normalCount: 0,
            powerOffCount: 0,
            emergencyCount: 0,
            errorCount: 0,
            workingCount: 0,
            ttlCount: 0,
            noneOperationCount: 0,
            powerDisconnectedCount: 0,
        }
        this.pathTrackingStore = new PathTrackingStore(this)
        this.impactHistoryStore = new ImpactHistoryStore(this)
        makeAutoObservable(this)
    }

    setShowTtlDeviceStatus = (show: boolean) => {
        this.showTtlDeviceStatus = show
    }
    setShowDeviceStatusInfoList = (show: boolean) => {
        this.showDeviceStatusInfoList = show
    }

    setShowStatusChart = (show: boolean) => {
        this.showStatusChart = show
    }

    setShowEventCount = (show: boolean) => {
        this.showEventCount = show
    }
    setMap = (map: kakao.maps.Map) => {
        this.map = map
    }

    selectDevice = (device: RealDevice | undefined) => {
        if (device && !isValidStringCoordinate(device.communicationDevice?.currentLatLng)) {
            showNotification(getCoordinateErrorMessage(device.communicationDevice?.currentLatLng) || "위치 정보가 없습니다.", NotificationType.WARNING)
        }
        this.selectedDevice = device
    }

    setShowDeviceStatusInListFilter = (filter: DeviceStatus[] | null) => {
        this.deviceStatusFilterCondition = filter
    }

    setShowRealDeviceInfo = (show: boolean, device?: RealDevice) => {
        this.selectedDevice = device
        this.setShowSelectedRealDeviceInfo(true)
    }

    setShowSelectedRealDeviceInfo = (show: boolean) => {
        this.showRealDeviceInfo = (show && !!this.selectedDevice)
        if (show) {
            this.setShowImpactHistory(false)
            this.setShowPathTrackingInfo(false)
        }
        // if (!show) {
        //     this.selectDevice(undefined)
        // } else {
        //     this.setShowImpactHistory(false)
        //     this.setShowPathTrackingInfo(false)
        // }
    }

    setShowImpactHistory = (show: boolean) => {
        this.showImpactHistory = (show && !!this.selectedDevice)
        if (!show) {
            this.impactHistoryStore.setPage(0)
            this.impactHistoryStore.setLocation(undefined)
        } else {
            this.setShowSelectedRealDeviceInfo(false)
            this.setShowPathTrackingInfo(false)
        }
    }

    setShowPathTrackingInfo = (show: boolean) => {
        this.showPathTrackingInfo = (show && !!this.selectedDevice)
        if (!show) {
            this.pathTrackingStore.setPage(0)
            this.pathTrackingStore.setLocation(undefined)
            this.pathTrackingStore.setSearchDate(new Date())
        } else {
            this.setShowSelectedRealDeviceInfo(false)
            this.setShowImpactHistory(false)
        }
    }

    setSearchKeyword = (keyword: undefined | string) => {
        this.searchKeyword = keyword
    }

    setCounter = (counter: Counter) => {
        this.counter = counter
    }

    setHideConditionForEmergencyDevice = (eui: string, condition: HidingConditionKey) => {
        const time = getHidingConditionTime(condition)
        this.conditionToHide.set(eui, time)
        console.log("condition 설정 : ", toJS(this.conditionToHide))
    }

    filteredDevices = (devices: RealDevice[]): RealDevice[] => {
        return []
    }

    setDeviceStatusCategory = (category: DeviceStatusCategory) => {
        this.deviceStatusCategory = category
    }
}

const getHidingConditionTime = (key: HidingConditionKey): Date => {
    const now = dayjs()
    switch (key) {
        case "5m":
            return now.add(5, "minute").toDate()
        case "10m":
            return now.add(10, "minute").toDate()
        case "20m":
            return now.add(20, "minute").toDate()
        case "30m":
            return now.add(30, "minute").toDate()
        case "60m":
            return now.add(60, "minute").toDate()
        case "1h":
            return now.add(1, "hour").toDate()
        case "2h":
            return now.add(2, "hour").toDate()
        case "4h":
            return now.add(4, "hour").toDate()
        case "8h":
            return now.add(8, "hour").toDate()
        case "12h":
            return now.add(12, "hour").toDate()
        case "24h":
            return now.add(24, "hour").toDate()
        default:
            return now.toDate()
    }
}

export type HidingConditionKey = "5m" | "10m" | "20m" | "30m" | "60m" | "1h" | "2h" | "4h" | "8h" | "12h" | "24h"


